import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { fmt } from '../data/metrics';
import {
  alignForecastToShifts,
  formatServiceEdgeHour,
  resolveTimelineShifts,
} from '../lib/shiftOverlay';
import {
  capacityOutlet,
  dayTotalsFromReservations,
  filterTimelineForOutlet,
  liveFloorFromReservations,
  outletGuestForecast,
  reservationsForOutlet,
  ribbonKpisForOutlet,
  sortLiveOutletOptions,
  type TimelineOutletOption,
} from '../lib/timelineOutletFilter';
import type {
  ConnectedSourceId,
  ForecastData,
  GroupKpis,
  KitchenBriefing,
  ServiceTimelineBenchmarks,
  ServiceTimelineOps,
  ServiceTimelineReservation,
} from '../types';
import { LiveBriefingPanel } from './LiveBriefingPanel';
import { LiveOutletPicker } from './LiveOutletPicker';
import { ReservationFocusDrawer } from './ReservationFocusDrawer';
import { GuestFlowBar } from './ribbon/GuestFlowBar';
import { pointGuests, RibBenchmark, RibStat, TimelineBand } from './ribbon/ribbonPieces';
import { ServiceTimelineGantt } from './ServiceTimelineGantt';
import { AnimatedNumber } from './shared/AnimatedNumber';
import { Icon, Ring } from './shared/Icon';

type TimelineView = 'curve' | 'breakdown';

export function ServiceRibbon({
  kpis,
  forecast,
  connected,
  benchmarks,
  serviceTimeline,
  kitchenBriefing = null,
  outlets = [],
  pickerOutlets,
  liveOutletId = null,
  onLiveOutletIdChange,
  onReservationDrawerVisibilityChange,
  onDrill,
}: {
  kpis: GroupKpis;
  forecast: ForecastData | null;
  connected: ConnectedSourceId[];
  benchmarks?: ServiceTimelineBenchmarks | null;
  serviceTimeline?: ServiceTimelineOps | null;
  kitchenBriefing?: KitchenBriefing | null;
  outlets?: TimelineOutletOption[];
  /**
   * Fleet list for the restaurant picker (may be wider than the current live
   * snapshot — e.g. after focusing one outlet). Defaults to `outlets`.
   */
  pickerOutlets?: Array<{
    id: string;
    name: string;
    closed?: boolean;
    city?: string;
    region?: string;
  }>;
  /** Controlled Live restaurant — whole Live tab is this outlet. */
  liveOutletId?: string | null;
  onLiveOutletIdChange?: (id: string | null) => void;
  onReservationDrawerVisibilityChange?: (open: boolean) => void;
  onDrill?: (key: string) => void;
}) {
  const { t } = useTranslation('live');
  const hasPos = connected.includes('pos');
  const [view, setView] = useState<TimelineView>('curve');
  // Keep Gantt mounted after first open so the door animation can collapse it.
  const [breakdownReady, setBreakdownReady] = useState(false);
  const [selectedReservation, setSelectedReservation] =
    useState<ServiceTimelineReservation | null>(null);
  const reservationDrawerOpen = selectedReservation !== null;
  useEffect(() => {
    onReservationDrawerVisibilityChange?.(reservationDrawerOpen);
  }, [onReservationDrawerVisibilityChange, reservationDrawerOpen]);
  useEffect(
    () => () => onReservationDrawerVisibilityChange?.(false),
    [onReservationDrawerVisibilityChange],
  );
  // Snapshot outlets power KPIs; picker list can stay the full fleet after focus.
  const outletChoices = useMemo(
    () => sortLiveOutletOptions(outlets),
    [outlets],
  );
  const pickerChoices = useMemo(
    () => sortLiveOutletOptions(pickerOutlets?.length ? pickerOutlets : outlets),
    [pickerOutlets, outlets],
  );
  const multiOutlet = pickerChoices.length > 1;

  // Fallback when parent hasn't lifted selection yet.
  const [localOutletId, setLocalOutletId] = useState<string | null>(null);
  useEffect(() => {
    if (onLiveOutletIdChange) return;
    if (!multiOutlet) {
      setLocalOutletId(pickerChoices[0]?.id ?? null);
      return;
    }
    setLocalOutletId(prev => {
      if (prev && pickerChoices.some(o => o.id === prev)) return prev;
      return pickerChoices[0]?.id ?? null;
    });
  }, [multiOutlet, pickerChoices, onLiveOutletIdChange]);

  const timelineOutletId = onLiveOutletIdChange
    ? liveOutletId
    : localOutletId;
  const setTimelineOutletId = (id: string | null) => {
    onLiveOutletIdChange?.(id);
    if (!onLiveOutletIdChange) setLocalOutletId(id);
  };

  useEffect(() => {
    setSelectedReservation(null);
  }, [timelineOutletId, view]);

  if (!forecast) {
    return (
      <section className="ribbon">
        <div className="sec-head">
          <h2>{t('ribbon.title')}</h2>
        </div>
        <p className="loading-text">{t('ribbon.loading')}</p>
      </section>
    );
  }

  // Live is always one restaurant — pin KPIs / curve / Gantt to it.
  const selectedOutletId =
    timelineOutletId ??
    (outletChoices.length === 1 ? outletChoices[0]?.id ?? null : null);
  const selectedOutlet =
    outletChoices.find(o => o.id === selectedOutletId) ?? null;
  // Capacity always from a real snapshot outlet (seats/tables), not a stub.
  const cap = capacityOutlet(outlets, selectedOutletId) ?? selectedOutlet;
  const kpiOutlet: TimelineOutletOption | null =
    selectedOutlet && cap
      ? {
          ...selectedOutlet,
          seats: cap.seats,
          tables: cap.tables,
          // Prefer snapshot day-book when the selection itself was a stub.
          coversToday: selectedOutlet.coversToday ?? cap.coversToday,
          bookedAhead: selectedOutlet.bookedAhead ?? cap.bookedAhead,
          upcoming: selectedOutlet.upcoming ?? cap.upcoming,
          seated: selectedOutlet.seated || cap.seated,
          occTables: selectedOutlet.occTables || cap.occTables,
          spendCover: selectedOutlet.spendCover ?? cap.spendCover,
          avgSeatMin: selectedOutlet.avgSeatMin ?? cap.avgSeatMin,
          lostGapCovers: selectedOutlet.lostGapCovers ?? cap.lostGapCovers,
        }
      : selectedOutlet;
  const scopedTimeline = filterTimelineForOutlet(
    serviceTimeline,
    selectedOutletId,
  );
  const timelineShifts = resolveTimelineShifts(scopedTimeline);
  const fc = alignForecastToShifts(
    outletGuestForecast(
      forecast,
      serviceTimeline ?? null,
      selectedOutletId,
      kpiOutlet,
    ),
    timelineShifts,
  );
  const outletReservations = reservationsForOutlet(
    serviceTimeline,
    selectedOutletId,
  );
  const dayTotals = dayTotalsFromReservations(
    outletReservations,
    fc.openH,
    fc.closeH,
    fc.nowH,
  );
  const liveFloor = liveFloorFromReservations(
    outletReservations,
    fc.openH,
    fc.closeH,
    fc.nowH,
  );
  const scopedKpis = ribbonKpisForOutlet(
    kpis,
    kpiOutlet,
    dayTotals,
    liveFloor,
  );
  const nowGuests = scopedKpis.seated;
  // Scope benchmarks are fleet-level — hide them when pinned to one outlet.
  const bench = selectedOutletId ? null : benchmarks;
  const peakGuests = pointGuests(fc.peak);
  const clock = fc.nowH * 60;
  const benchGuests = selectedOutletId ? null : (bench?.seated ?? null);
  const lostGapCovers = selectedOutlet?.lostGapCovers ?? 0;
  const drill = (key: string) => (onDrill ? () => onDrill(key) : undefined);

  // Day total from timeline+outlet; never under-count vs served + next-2h.
  const bookedByClose = Math.max(
    fc.projectedCovers,
    dayTotals.total,
    scopedKpis.covers + scopedKpis.incoming,
  );
  const stillBooked = Math.max(
    0,
    bookedByClose - scopedKpis.covers - scopedKpis.incoming,
  );

  return (
    <>
    <section className="ribbon" data-ftue="live-ribbon">
      <div className="sec-head" data-ftue="live-timeline">
        <h2>{t('ribbon.title')}</h2>
        <span className="sh-meta">
          {t('ribbon.meta')}
          {bench?.regimeLabel && bench.sampleWeeks > 0
            ? t('ribbon.vsAvg', { label: bench.regimeLabel, weeks: bench.sampleWeeks })
            : ''}
        </span>
        {multiOutlet && (
          <LiveOutletPicker
            outlets={pickerChoices}
            value={selectedOutletId}
            onChange={id => setTimelineOutletId(id)}
          />
        )}
        <div className="rib-view-trail">
          <div className="rib-view-switch" role="group" aria-label={t('ribbon.viewAria')}>
            <button
              type="button"
              className={view === 'curve' ? 'on' : ''}
              aria-pressed={view === 'curve'}
              onClick={() => setView('curve')}
            >
              <Icon name="activity" size={15} stroke={2.2} className="rib-view-ic" />
              <span className="rib-view-lab">
                <span className="rib-view-title">{t('ribbon.loadCurve')}</span>
                <span className="rib-view-sub">{t('ribbon.guestPressure')}</span>
              </span>
            </button>
            <button
              type="button"
              className={view === 'breakdown' ? 'on' : ''}
              aria-pressed={view === 'breakdown'}
              onClick={() => {
                setBreakdownReady(true);
                setView('breakdown');
              }}
            >
              <Icon name="table" size={15} stroke={2.2} className="rib-view-ic" />
              <span className="rib-view-lab">
                <span className="rib-view-title">{t('ribbon.bookings')}</span>
                <span className="rib-view-sub">{t('ribbon.whoWhen')}</span>
              </span>
            </button>
          </div>
        </div>
      </div>

      <div className={`ribbon-card rib-doors view-${view}`}>
        {/* Top door: full curve ↔ minified curve (shrinks upward). */}
        <div className="rib-door-top">
          <div className={`rib-band-row${view === 'breakdown' ? ' is-compact' : ''}`}>
            {view === 'curve' && (
              <span className="rib-edge">
                opens {formatServiceEdgeHour(fc.openH)}
              </span>
            )}
            <TimelineBand
              fc={fc}
              nowGuests={nowGuests}
              benchGuests={benchGuests}
              shifts={timelineShifts}
              compact={view === 'breakdown'}
            />
            {view === 'curve' && (
              <span className="rib-edge">
                closes {formatServiceEdgeHour(fc.closeH)}
              </span>
            )}
          </div>
        </div>

        {/* Middle door: Breakdown Gantt slides open between curve and KPIs. */}
        <div
          className="rib-door-mid"
          aria-hidden={view !== 'breakdown'}
        >
          {breakdownReady && (
            <ServiceTimelineGantt
              forecast={fc}
              timeline={scopedTimeline}
              nowGuests={nowGuests}
              showOutlet={false}
              onSelectReservation={setSelectedReservation}
            />
          )}
        </div>

        {/* Bottom door: KPI columns shift down as the Gantt appears. */}
        <div className="rib-door-bot">
        <div className="ribbon-cols">
          {/* ---------- SO FAR TODAY ---------- */}
          <div className="rib-col past">
            <div className="rib-col-head">
              <span className="rib-col-lab">{t('ribbon.soFar')}</span>
            </div>
            <RibStat
              tense="fact"
              value={fmt.num(scopedKpis.covers)}
              label={t('ribbon.guestsServed')}
              onClick={drill('covers')}
              benchmark={
                <RibBenchmark
                  baseline={bench?.covers}
                  current={scopedKpis.covers}
                  formatValue={v => fmt.num(v)}
                />
              }
            />
            <RibStat
              tense="fact"
              value={fmt.num(lostGapCovers)}
              label="unrebooked cancels & no-shows"
              onClick={drill('noshow')}
            />
            {!hasPos && (
              <RibStat
                tense="fact"
                value={
                  <>
                    {scopedKpis.seatTime}
                    <span className="rib-u">m</span>
                  </>
                }
                label="avg dwell so far"
                onClick={drill('seatTime')}
                benchmark={
                  <RibBenchmark
                    baseline={bench?.seatTime}
                    current={scopedKpis.seatTime}
                    formatValue={v => `${v}m`}
                    invert
                    deltaMode="min"
                  />
                }
              />
            )}
          </div>

          {/* ---------- RIGHT NOW ---------- */}
          <div className="rib-col now">
            <div className="rib-col-head">
              <span className="rib-col-lab">
                <span className="rib-pulse" />
                Right now
              </span>
              <span className="rib-col-sub">{fmt.clock(clock)} · live floor</span>
            </div>
            <div className="rib-now-hero">
              <div
                className={`rib-now-ring${onDrill ? ' click' : ''}`}
                onClick={drill('occupancy')}
                role={onDrill ? 'button' : undefined}
              >
                <Ring pct={scopedKpis.occupancy} size={74} stroke={7} color="var(--accent)">
                  {`${scopedKpis.occupancy}%`}
                </Ring>
                <RibBenchmark
                  baseline={bench?.occupancy}
                  current={scopedKpis.occupancy}
                  formatValue={v => `${v}%`}
                />
              </div>
              <div className="rib-now-side">
                <div className="rib-now-row">
                  <div className="rib-now-main">
                    <b>
                      <AnimatedNumber value={scopedKpis.seated} />
                    </b>
                    <span>
                      of {fmt.num(scopedKpis.seats)} seats
                      {bench?.seats != null && (
                        <span className="rib-cap-bench">
                          {' '}
                          (avg {fmt.num(bench.seats)})
                        </span>
                      )}
                    </span>
                  </div>
                  <RibBenchmark
                    baseline={bench?.seated}
                    current={scopedKpis.seated}
                    formatValue={v => fmt.num(v)}
                  />
                </div>
                <div className="rib-now-row">
                  <div className="rib-now-main">
                    <b>{scopedKpis.occTables}</b>
                    <span>
                      of {scopedKpis.tables} tables
                      {bench?.tables != null && (
                        <span className="rib-cap-bench">
                          {' '}
                          (avg {bench.tables})
                        </span>
                      )}
                    </span>
                  </div>
                  <RibBenchmark
                    baseline={bench?.occTables}
                    current={scopedKpis.occTables}
                    formatValue={v => String(v)}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* ---------- STILL TO COME ---------- */}
          <div className="rib-col future">
            <div className="rib-col-head">
              <span className="rib-col-lab">{t('ribbon.stillToCome')}</span>
              <span className="rib-col-sub">{t('ribbon.fromReservations')}</span>
            </div>
            <RibStat
              tense="booked"
              value={
                <span style={{ color: 'var(--accent)' }}>+{scopedKpis.incoming}</span>
              }
              label={t('ribbon.bookedNext2h')}
              onClick={drill('incoming')}
              benchmark={
                <RibBenchmark
                  baseline={bench?.incoming}
                  current={scopedKpis.incoming}
                  formatValue={v => `+${v}`}
                />
              }
            />
            <RibStat
              tense="booked"
              value={fmt.num(bookedByClose)}
              label="booked by close"
              onClick={drill('covers')}
              benchmark={
                <RibBenchmark
                  baseline={bench?.projectedCovers}
                  current={bookedByClose}
                  formatValue={v => fmt.num(v)}
                />
              }
            />
            <RibStat
              tense="booked"
              value={
                <>
                  {fmt.num(peakGuests)}
                  <span className="rib-u"> @ </span>
                  {fmt.clock(fc.peakMin)}
                </>
              }
              label="peak guests · booked"
              onClick={drill('occupancy')}
              benchmark={
                bench?.seated != null ? (
                  <RibBenchmark
                    baseline={bench.seated}
                    current={peakGuests}
                    formatValue={v => fmt.num(v)}
                  />
                ) : null
              }
            />
            
          </div>
        </div>

        <GuestFlowBar
          served={scopedKpis.covers}
          incoming={scopedKpis.incoming}
          stillBooked={stillBooked}
          bookedByClose={bookedByClose}
        />
        </div>
      </div>

      {selectedReservation && (
        <ReservationFocusDrawer
          reservation={selectedReservation}
          onClose={() => setSelectedReservation(null)}
        />
      )}
    </section>
    <LiveBriefingPanel
      timeline={scopedTimeline}
      kitchenBriefing={kitchenBriefing}
      outletId={selectedOutletId}
      onSelectReservation={setSelectedReservation}
    />
    </>
  );
}
